import { useState, useEffect } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Home, FolderOpen, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import Alert from "../components/common/ui/alert/Alert";
import { useAlert } from "../hooks/useAlert";
import { apiClient } from "../utils/apiClient/index.ts";
import type { OrganizationPublic } from "../types";

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

type NavItem = {
  name: string;
  icon: React.ReactNode;
  path: string;
};

const clientNavItems: NavItem[] = [
  {
    name: "Dashboard",
    icon: <Home className="w-5 h-5" />,
    path: "/portal/dashboard",
  },
  {
    name: "Documents",
    icon: <FolderOpen className="w-5 h-5" />,
    path: "/portal/documents",
  },
];

export const ClientPortalLayout: React.FC = () => {
  const location = useLocation();
  const { currentUser, signOut } = useAuth();
  const { alert } = useAlert();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [organization, setOrganization] = useState<OrganizationPublic | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(true);

  const isActive = (path: string) => location.pathname === path;

  // Fetch organization data for branding
  useEffect(() => {
    const loadOrganization = async () => {
      if (!currentUser?.organizations?.[0]?.id) return;

      try {
        const orgData = await apiClient.getOrganizationPublic(
          currentUser.organizations[0].id,
        );
        setOrganization(orgData);
        setLoading(false);
      } catch (err) {
        console.error("Failed to load organization:", err);
      }
    };

    loadOrganization();
  }, [currentUser?.organizations]);

  if (loading) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Top Header */}
      <header className="sticky top-0 z-40 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between px-4 py-4 lg:px-6">
          <div className="flex items-center gap-4">
            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>

            {/* Logo / Branding */}
            <div className="flex flex-col">
              <Link to="/portal/dashboard" className="flex items-center gap-2">
                {organization?.logoUrl ? (
                  <img
                    src={organization.logoUrl}
                    alt={organization.name}
                    className="max-w-[250px] object-contain"
                  />
                ) : (
                  <>
                    <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                      <span className="text-white font-bold text-sm">
                        {organization?.name
                          ? getInitials(organization.name)
                          : "CP"}
                      </span>
                    </div>
                    <span className="font-semibold text-gray-900 dark:text-white hidden sm:block">
                      {organization?.name
                        ? `${organization.name} - Client Portal`
                        : "Client Portal"}
                    </span>
                  </>
                )}
              </Link>
              {!organization?.logoUrl && (
                <Link
                  to="/"
                  className="text-[10px] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 text-right"
                >
                  Powered by Casekeep
                </Link>
              )}
            </div>
          </div>

          {/* User Menu */}
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {currentUser?.fullName || currentUser?.email}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Client</p>
            </div>
            <button
              onClick={signOut}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex border-t border-gray-200 dark:border-gray-700">
          <div className="flex px-6">
            {clientNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex items-center gap-2 px-4 py-3 border-b-2 transition-colors
                  ${
                    isActive(item.path)
                      ? "border-blue-600 text-blue-600 dark:text-blue-400"
                      : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                  }
                `}
              >
                {item.icon}
                <span className="font-medium text-sm">{item.name}</span>
              </Link>
            ))}
          </div>
        </nav>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/50"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div
            className="absolute left-0 top-[73px] bottom-0 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <nav className="space-y-1">
              {clientNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                    ${
                      isActive(item.path)
                        ? "bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    }
                  `}
                >
                  {item.icon}
                  <span className="font-medium text-sm">{item.name}</span>
                </Link>
              ))}
            </nav>

            {/* User Info in Mobile Menu */}
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="px-4">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {currentUser?.fullName || currentUser?.email}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Client Account
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="p-4 mx-auto max-w-7xl md:p-6">
        {alert && <Alert alert={alert} />}
        <Outlet />
      </main>
    </div>
  );
};

export default ClientPortalLayout;
